"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidatorCondBuilder = void 0;
const get_1 = __importDefault(require("lodash/get"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const isNil_1 = __importDefault(require("lodash/isNil"));
const isString_1 = __importDefault(require("lodash/isString"));
const TSUtils_1 = require("../../native/TSUtils");
var ParamType;
(function (ParamType) {
    ParamType["FIELD"] = "FIELD";
    ParamType["PROPERTY"] = "PROPERTY";
    ParamType["VALUE"] = "VALUE";
})(ParamType || (ParamType = {}));
var Modifier;
(function (Modifier) {
    Modifier["IS"] = "IS";
    Modifier["IS_NOT"] = "IS_NOT";
})(Modifier || (Modifier = {}));
var ConditionType;
(function (ConditionType) {
    ConditionType["NIL"] = "NIL";
    ConditionType["EMPTY"] = "EMPTY";
    ConditionType["EQUALS"] = "EQUAL";
})(ConditionType || (ConditionType = {}));
/**
 * Builder for simple validator conditions
 * If your condition is too complex, please don't use this builder
 * NOTE: Some conditions require more than 1 params
 */
class ValidatorCondBuilder {
    constructor() {
        this._params = [];
    }
    // =============================================================================
    // Params
    // =============================================================================
    /**
     * This field's value
     */
    paramField() {
        this._params.push({ valueType: ParamType.FIELD });
        return this;
    }
    /**
     * Another field's value
     */
    paramProperty(property) {
        this._params.push({ valueType: ParamType.PROPERTY, value: property });
        return this;
    }
    /**
     * Constant param value passed to this builder
     */
    param(value) {
        this._params.push({ valueType: ParamType.VALUE, value });
        return this;
    }
    // =============================================================================
    // Modifiers
    // =============================================================================
    get is() {
        this._modifier = Modifier.IS;
        return this;
    }
    get isNot() {
        this._modifier = Modifier.IS_NOT;
        return this;
    }
    // =============================================================================
    // Conditions
    // =============================================================================
    /**
     * Using lodash `isNil`
     */
    nil() {
        this._conditionType = ConditionType.NIL;
        return this;
    }
    /**
     * Using lodash `isEmpty`
     * Both passes if nil or empty
     */
    empty() {
        this._conditionType = ConditionType.EMPTY;
        return this;
    }
    /**
     * Using lodash `isEqual`
     */
    equals() {
        this._conditionType = ConditionType.EQUALS;
        return this;
    }
    // =============================================================================
    // Build
    // =============================================================================
    build() {
        // Doing assert before building the function to get faster feedback on bad input
        // Any assertions in the conditional function cannot execute until it's actually triggered
        this.assert();
        return (value, args) => {
            const values = this.getActualParamValues(value, args);
            let result = this.applyCondition(values);
            result = this.applyModifier(result);
            return result;
        };
    }
    // =============================================================================
    // Helpers
    // =============================================================================
    assertModifier() {
        if ((0, isNil_1.default)(this._modifier)) {
            throw new Error("Missing condition modifier");
        }
    }
    assertValuesCount(count) {
        if (this._params.length !== count) {
            throw new Error(`Condition should have ${count} values`);
        }
    }
    assertParams() {
        this._params.forEach((param) => {
            if (param.valueType === ParamType.PROPERTY && !(0, isString_1.default)(param.value)) {
                throw new Error("Property value must be a string");
            }
        });
        switch (this._conditionType) {
            case ConditionType.NIL:
            case ConditionType.EMPTY:
                this.assertValuesCount(1);
                return;
            case ConditionType.EQUALS:
                this.assertValuesCount(2);
                return;
        }
        TSUtils_1.TSUtils.exhaustiveCheck(this._conditionType);
        throw new Error("Unknown condition type");
    }
    assert() {
        this.assertModifier();
        this.assertParams();
    }
    getActualParamValues(value, args) {
        return this._params.map((param) => {
            switch (param.valueType) {
                case ParamType.FIELD:
                    return value;
                case ParamType.PROPERTY:
                    return (0, get_1.default)(args.object, param.value);
                case ParamType.VALUE:
                    return param.value;
            }
            TSUtils_1.TSUtils.exhaustiveCheck(param.valueType);
        });
    }
    applyCondition(values) {
        switch (this._conditionType) {
            case ConditionType.NIL:
                return (0, isNil_1.default)(values[0]);
            case ConditionType.EMPTY:
                return (0, isEmpty_1.default)(values[0]);
            case ConditionType.EQUALS:
                return (0, isEqual_1.default)(values[0], values[1]);
        }
        TSUtils_1.TSUtils.exhaustiveCheck(this._conditionType);
    }
    applyModifier(result) {
        switch (this._modifier) {
            case Modifier.IS:
                return result;
            case Modifier.IS_NOT:
                return !result;
        }
        TSUtils_1.TSUtils.exhaustiveCheck(this._modifier);
    }
}
exports.ValidatorCondBuilder = ValidatorCondBuilder;
//# sourceMappingURL=ValidatorCondBuilder.js.map